import { ActionIcon, Button, Divider, Group, Tooltip } from "@mantine/core";
import { Lightbulb, Monitor, RotateCcw, Sparkle, Trash2, Upload } from "lucide-react";

import type { AssetKind, AssetOrder, AssetSource } from "../../api/generated/model";
import { LIBRARY_ORDERS } from "../../platform/media/library-pages";
import { ProductFilter } from "../../platform/media/ProductFilter";
import { DisplaySettings, type GalleryLayout } from "../../ui/DisplaySettings";
import { FilterMenu } from "../../ui/FilterMenu";
import { SortMenu } from "../../ui/SortMenu";
import { TypeTabs } from "../../ui/TypeTabs";
import classes from "./library.module.css";

/** Live's source pills (`assetsTabsOptions`): Inspiration waits for the ads' inspiration files. */
const SOURCES = [
  { value: "all", label: "All" },
  { value: "generation", label: "Creative", icon: <Sparkle size={16} /> },
  { value: "upload", label: "Manual Uploads", icon: <Upload size={16} /> },
  { value: "inspiration", label: "Inspiration", icon: <Lightbulb size={16} />, disabled: true },
];

/** Live's Media filter (`mediaTypeOptions`), our kinds. */
const MEDIA = [
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
] as const satisfies readonly { value: AssetKind; label: string }[];

export interface LibraryView {
  kind: AssetKind | undefined;
  source: AssetSource | undefined;
  product: string | undefined;
  order: AssetOrder;
  trash: boolean;
}

interface LibraryToolbarProps {
  teamId: string;
  view: LibraryView;
  onView: (patch: Partial<LibraryView>) => void;
  columns: number;
  onColumns: (columns: number) => void;
  /** Live's layout and its rows' height (spec library-rows). */
  layout: GalleryLayout;
  onLayout: (layout: GalleryLayout) => void;
  rowHeight: number;
  onRowHeight: (height: number) => void;
}

/**
 * The library's filters (design-system.md 3.5, capture gaps/21; live's `ViewControls`), the rest
 * of the header row after "Projects" (`ProjectsLayout`): live's source pills, a divider, the
 * Product and Media filters and live's "Reset" while one of them is set; on the right live's sort,
 * the display settings (live's layout, its column count or row height) and our Trash, an icon that stays lit while the trash
 * is shown. Live has no Trash in its row: ours sits with the settings, so the row keeps live's
 * order and one line at a laptop's width with the trash on (#828 bug 27's follow-up).
 */
export function LibraryToolbar(props: LibraryToolbarProps) {
  const { teamId, view, onView, columns, onColumns, layout, onLayout, rowHeight, onRowHeight } = props;
  const filtered = view.kind !== undefined || view.product !== undefined;
  return (
    <div className={classes.views}>
      {/* One line as a unit, as live's Library `types-filter` (issue 828, item 18). */}
      <TypeTabs
        inline="spaced"
        label="Source"
        tabs={SOURCES}
        value={view.source ?? "all"}
        onChange={(value) =>
          onView({ source: value === "generation" || value === "upload" ? value : undefined })
        }
      />
      <Divider orientation="vertical" className={classes.divider} />
      <ProductFilter
        teamId={teamId}
        value={view.product}
        onChange={(product) => onView({ product })}
      />
      <FilterMenu
        label="Media"
        icon={<Monitor size={16} />}
        options={MEDIA}
        value={MEDIA.find((each) => each.value === view.kind)?.value}
        onChange={(kind) => onView({ kind })}
      />
      {filtered ? (
        <Button
          variant="text"
          size="compact-sm"
          leftSection={<RotateCcw size={16} />}
          onClick={() => onView({ kind: undefined, product: undefined })}
        >
          Reset
        </Button>
      ) : null}
      <Group gap={4} wrap="nowrap" className={classes.settings}>
        <SortMenu
          options={LIBRARY_ORDERS}
          value={view.order}
          onChange={(order) => onView({ order })}
        />
        <DisplaySettings
          layout={layout}
          onLayout={onLayout}
          columns={columns}
          onColumns={onColumns}
          rowHeight={rowHeight}
          onRowHeight={onRowHeight}
        />
        <Tooltip label={view.trash ? "Back to the library" : "Trash"} withArrow>
          <ActionIcon
            variant={view.trash ? "light" : "text"}
            size="lg"
            aria-label="Trash"
            aria-pressed={view.trash}
            onClick={() => onView({ trash: !view.trash })}
          >
            <Trash2 size={20} strokeWidth={1.5} aria-hidden />
          </ActionIcon>
        </Tooltip>
      </Group>
    </div>
  );
}
